import React, { useEffect, useRef } from 'react'
import { Link, generatePath } from 'react-router-dom'
import { useDraggable, useDroppable } from '@dnd-kit/core'
import { ISidebarResource, SidebarResourceKind } from 'types'
import { Options, Process, Spreadsheet } from 'components/icons'
import Tooltip from 'components/tooltip'
import {
  SIDEBAR_ROW,
  SIDEBAR_ROW_IDLE,
  SIDEBAR_ROW_ACTIVE,
  SIDEBAR_ICON,
  sidebarLabel,
  rowHighlightStyle,
  indentFor,
  SIDEBAR_DROP_LINE,
  dropLineStyle,
  SelectionJoin
} from 'components/sidebar/surface'
import { rowButtonProps } from 'components/sidebar/rowButton'
import { SelectionModifiers } from 'components/sidebar/components/folders/selection'
import { DropZone } from 'components/sidebar/components/folders/tree'
import {
  ROW_ACTIONS,
  ROW_ACTIONS_ROOM,
  RenameInput,
  RenameResult,
  RowAction
} from 'components/sidebar/components/folders/rowParts'
import { DOCUMENT, TABLE } from 'routes'

/**
 * Where the row links to. A table with no active default view has nothing to link to, so the row
 * renders unlinked instead of letting generatePath throw and crash the hub (issue #10063).
 */
export const resourcePath = (hubId: string, resource: ISidebarResource): string | null => {
  if (resource.kind === 'document') {
    return generatePath(DOCUMENT, { projectId: hubId, processId: resource.publicId })
  }
  if (!resource.defaultViewId) return null
  return generatePath(TABLE, { projectId: hubId, tableId: resource.publicId, viewId: resource.defaultViewId })
}

interface ResourceRowContentProps {
  kind: SidebarResourceKind
  name: string
  active?: boolean
}

const ResourceIcon: React.FC<{ kind: SidebarResourceKind }> = ({ kind }) => (
  <span className={SIDEBAR_ICON}>{kind === 'document' ? <Process /> : <Spreadsheet />}</span>
)

/** A resource row's look, the icon and the name, with no behaviour, so a drag preview can reuse it. */
export const ResourceRowContent: React.FC<ResourceRowContentProps> = ({ kind, name, active = false }) => (
  <>
    <ResourceIcon kind={kind} />
    <Tooltip content={name} showOnlyIfTruncated delay={300}>
      <span className={sidebarLabel(active)}>{name}</span>
    </Tooltip>
  </>
)

interface ResourceRowProps {
  id: string
  kind: SidebarResourceKind
  name: string
  /** Where the row links to (resourcePath); null for a row with nothing to open. */
  href: string | null
  depth: number
  /** The page on screen is this resource. */
  active: boolean
  /** Part of the sidebar's multi-selection. */
  selected: boolean
  /** Which neighbours are selected too, so a run of selected rows draws as one block. */
  selectionJoin?: SelectionJoin
  /** What a folder's Delete & archive would take away, while it's pointed at: a red wash. */
  previewed?: boolean
  /** The active row, while rows are selected and it isn't one: it shows no highlight, so it doesn't look selected. */
  outsideSelection: boolean
  /** Whether the viewer may move this row: select and drag it (hub Owners, or an Owner of the resource). */
  canMove: boolean
  /** Whether a drag may drop beside this row (anyone who can move something). */
  droppable: boolean
  /** Whether F2 opens the rename box: the viewer owns it and it is the only row being acted on. */
  canRename: boolean
  /** Showing the inline rename box, in place of the link. Controlled by the tree. */
  renaming: boolean
  /** A drag would drop beside this row: a line on that edge. */
  dropIndicator?: DropZone | null
  /** Part of the drag in progress. */
  dragging?: boolean
  brandColour?: string
  /**
   * Runs before the row navigates. Returning true claims the click (a selection gesture), and the
   * link is then not followed.
   */
  onSelectClick: (event: SelectionModifiers, id: string) => boolean
  onRenameStart: (id: string) => void
  /** The rename box closed, and how. */
  onRenameEnd: (id: string, result: RenameResult) => void
  /** A right-click on the row; `dragging` while a drag is on. */
  onContextMenu?: (event: React.MouseEvent<HTMLElement>, id: string, dragging: boolean) => void
  /** Opens the row's menu from its ⋯. Unset when there's nothing in it for this viewer. */
  onOpenMenu?: (id: string, button: HTMLElement) => void
}

/**
 * A document or table in the sidebar tree. F2 renames it, for its Owners; a double-click doesn't, as
 * it opens the link.
 *
 * Memoised, with only primitives and stable callbacks for props, so a change to one row (selecting
 * it, say) doesn't re-render the thousands of others in a big hub.
 */
const ResourceRow: React.FC<ResourceRowProps> = ({
  id,
  kind,
  name,
  href,
  depth,
  active,
  selected,
  selectionJoin = 'none',
  previewed = false,
  outsideSelection,
  canMove,
  droppable,
  canRename,
  renaming,
  dropIndicator = null,
  dragging = false,
  brandColour,
  onSelectClick,
  onRenameStart,
  onRenameEnd,
  onContextMenu,
  onOpenMenu
}) => {
  // The row itself, for focus after a rename.
  const rowRef = useRef<HTMLElement | null>(null)
  const refocus = useRef(false)
  // No dragging out of a rename: the pointer and keys belong to the text box.
  const drag = useDraggable({ id, disabled: !canMove || renaming })
  const { setNodeRef: setDropRef } = useDroppable({ id, disabled: !droppable })
  // A drag is on (this row's or another's). Its keys are the drag's, not the row's.
  const dragActive = drag.active !== null
  // The page on screen, unless it's been left out of a selection: then it looks like any other row.
  const highlighted = active && !outsideSelection
  const setWrapperRef = (node: HTMLDivElement | null): void => {
    drag.setNodeRef(node)
    setDropRef(node)
  }
  const setRowRef = (node: HTMLElement | null): void => {
    rowRef.current = node
  }

  // A rename closed from the keyboard hands focus back to the row, once it is a link again.
  useEffect(() => {
    if (renaming || !refocus.current) return
    refocus.current = false
    rowRef.current?.focus()
  }, [renaming])

  /** F2 on the focused row opens the rename box. True when it did, and the key is spent. */
  const renameKey = (event: React.KeyboardEvent<HTMLElement>): boolean => {
    if (event.key !== 'F2' || !canRename || dragActive) return false
    event.preventDefault()
    onRenameStart(id)
    return true
  }

  // No ⋯ beside a rename box.
  const openMenu = renaming ? undefined : onOpenMenu
  // No pointer-events guard while the hub loads: the tree wrapper already disables them. Of
  // dnd-kit's `attributes` only the keyboard instructions are used; the rest would turn the link
  // into a button.
  const rowProps = {
    ref: setRowRef,
    className: `${SIDEBAR_ROW} ${highlighted ? SIDEBAR_ROW_ACTIVE : SIDEBAR_ROW_IDLE} relative ${ROW_ACTIONS_ROOM[openMenu ? 1 : 0]} ${href && !renaming ? '' : 'cursor-default'} ${dragging ? 'opacity-50' : ''}`,
    style: {
      paddingLeft: indentFor(depth),
      ...rowHighlightStyle({ active: highlighted, selected, previewed, join: selectionJoin }, brandColour)
    },
    'data-selected': selected,
    // What focus is handed back to by id, once a move has re-rendered the row somewhere else.
    'data-row-id': id,
    'data-drop-indicator': dropIndicator ?? undefined,
    'data-draggable': canMove,
    'aria-describedby': canMove ? drag.attributes['aria-describedby'] : undefined,
    onContextMenu: onContextMenu
      ? (event: React.MouseEvent<HTMLElement>) => onContextMenu(event, id, dragActive)
      : undefined
  }
  const rowContents = (
    <>
      {dropIndicator === 'before' || dropIndicator === 'after' ? (
        <span className={SIDEBAR_DROP_LINE} style={dropLineStyle(dropIndicator, indentFor(depth), brandColour)} />
      ) : null}
      <ResourceRowContent kind={kind} name={name} active={highlighted} />
    </>
  )

  /** The row: a rename box, or the link to the page, or (with no page) a row that only selects. */
  const row = (): React.ReactNode => {
    if (renaming) {
      // A plain container while renaming: a text box can't go inside a link or a button.
      return (
        <div {...rowProps}>
          <ResourceIcon kind={kind} />
          <RenameInput
            initialName={name}
            label={kind === 'document' ? 'Document name' : 'Table name'}
            testId="resource-rename-input"
            onDone={(result, byKey) => {
              refocus.current = byKey
              onRenameEnd(id, result)
            }}
          />
        </div>
      )
    }
    if (href) {
      return (
        <Link
          {...rowProps}
          to={href}
          aria-current={active ? 'page' : undefined}
          onClick={(event) => {
            if (onSelectClick(event, id)) event.preventDefault()
          }}
          onKeyDown={renameKey}
        >
          {rowContents}
        </Link>
      )
    }
    // Nothing to open. Someone who can move it can still focus and select it from the keyboard; for
    // anyone else it does nothing, so it isn't offered as a button.
    if (!canMove) return <div {...rowProps}>{rowContents}</div>
    const button = rowButtonProps((event) => {
      if (!dragActive) onSelectClick(event, id)
    })
    return (
      <div
        {...rowProps}
        {...button}
        onKeyDown={(event) => {
          if (!renameKey(event)) button.onKeyDown(event)
        }}
      >
        {rowContents}
      </div>
    )
  }

  // The id is what the tree scrolls to when this resource's page opens. The drag listeners sit on
  // this wrapper, so the row's own click and key handling stay as they are; Alt/Option + ↑/↓ on the
  // focused row bubbles up here and picks it up.
  return (
    <div id={id} ref={setWrapperRef} className="relative group w-full" {...(renaming ? {} : drag.listeners)}>
      {row()}
      {openMenu && (
        // Beside the row rather than inside it: a button can't go inside a link.
        <span className={ROW_ACTIONS}>
          <RowAction
            tooltip="More actions"
            label={`More actions for ${name}`}
            testId="sidebar-row-more"
            onClick={(button) => openMenu(id, button)}
          >
            <Options />
          </RowAction>
        </span>
      )}
    </div>
  )
}

export default React.memo(ResourceRow)
